import { GridApiClient} from "../api/grid-api-client";
import {
  SigningManager,
  SignRequest,
  TransactionPayload,
  TransactionResult,
  SignAndSendRequest,
} from "../managers/signing-manager";
import { base64ToBytes, bytesToBase64 } from "../utils/encoding";
import {
  AuthorizationError,
  GridError,
  SendTransactionRequest,
  PrepareArbitraryTransactionRequest,
  PrepareArbitraryTransactionQueryParams,
} from "../types";
import {
  TransactionSubmissionResponse,
  CreateProposalRequest,
  CreateProposalResponse,
  VoteProposalRequest,
  VoteProposalResponse,
  ExecuteProposalRequest,
  ExecuteProposalResponse
} from "../types/common";
import { VersionedTransaction } from "@solana/web3.js";
import { TransactionResponse } from "../managers/signing-manager";

/**
 * @hidden
 * Service handling transaction operations
 */
export class TransactionService {
  private apiClient: GridApiClient;
  private signingManager: SigningManager;

  constructor(apiClient: GridApiClient, signingManager: SigningManager) {
    this.apiClient = apiClient;
    this.signingManager = signingManager;
  }

  /**
   * Extract a Solana VersionedTransaction ready to be signed using solana web3.js
   *
   * @param transactionData - Grid transaction payload containing the transaction
   * @returns Deserialized VersionedTransaction ready for signing
   *
   * @example
   * ```typescript
   * const preparedTx = await gridClient.prepareArbitraryTransaction(accountAddress, prepareRequest);
   * const signableTransaction = gridClient.extractSignableTransaction(preparedTx);
   * ```
   */
  extractSignableTransaction(transactionData: TransactionPayload): VersionedTransaction {
    const preparedTxBytes = base64ToBytes(transactionData.transaction);
    const preparedTransaction = VersionedTransaction.deserialize(preparedTxBytes);

    return preparedTransaction;
  }

  /**
   * Set an externally signed transaction into Grid transaction payload so you can submit it via grid api.
   *
   * @param transactionData - Original Grid transaction data
   * @param externallySignedTransaction - The signed VersionedTransaction
   * @returns Updated Grid transaction payload with signed transaction data
   *
   * @example
   * ```typescript
   * transaction.sign([externalSigner]);
   *
   * const signedTxData = gridClient.setExternallySignedTransaction(
   *   originalTxData,
   *   transaction
   * );
   * ```
   */
  setExternallySignedTransaction(transactionData: TransactionPayload, externallySignedTransaction: VersionedTransaction): TransactionPayload {
    return {
      transaction: bytesToBase64(externallySignedTransaction.serialize()),
      transactionSigners: transactionData.transactionSigners,
      kmsPayloads: transactionData.kmsPayloads
    };
  }

  /**
   * Automatically selects the appropriate authentication provider based on the
   * session context and available session secrets.
   *
   * @param request - Sign request containing transaction payload and session secrets
   * @returns Promise resolving to transaction result with signatures and metadata
   *
   * @example
   * ```typescript
   * try {
   *   const authResult = await gridClient.completeAuth(payload);
   *   const preparedTx = await gridClient.prepareArbitraryTransaction(accountAddress, {
   *     transaction: 'base64-encoded-transaction'
   *   });
   *
   *   const signResult = await gridClient.sign({
   *     sessionSecrets: sessionSecrets,
   *     transactionPayload: preparedTx,
   *     session: authResult.authentication
   *   });
   * } catch (error) {
   *   if (error instanceof ApiError) {
   *     console.error('Signing failed:', error.message);
   *   }
   * }
   * ```
   *
   * @category Transaction Signing
   */
  async sign(request: SignRequest): Promise<TransactionResult> {
    try {
      const result = await this.signingManager.sign(request);
      return result;
    } catch (error: any) {
      if (error instanceof AuthorizationError) {
        throw error;
      }
      throw new AuthorizationError(
        `Signing failed: ${error.message}`,
        "SIGNING_FAILED"
      );
    }
  }

  /**
   * Sign and send a transaction. Automatically selects the appropriate authentication provider based on the
   * session context and available session secrets
   *
   * @param request - Sign and send request containing transaction payload and session secrets
   * @returns Promise resolving to transaction submission result with blockchain signature
   *
   * @example
   * ```typescript
   * try {
   *   const authResult = await gridClient.completeAuth(payload);
   *   const preparedTx = await gridClient.prepareArbitraryTransaction(accountAddress, {
   *     transaction: 'base64-encoded-transaction'
   *   });
   *
   *   const result = await gridClient.signAndSend({
   *     sessionSecrets: sessionSecrets,
   *     transactionPayload: preparedTx,
   *     session: authResult.authentication,
   *     address: accountAddress
   *   });
   *   console.log('Transaction signature:', result.transactionSignature);
   * } catch (error) {
   *   if (error instanceof ApiError) {
   *     console.error('Transaction failed:', error.message);
   *   }
   * }
   * ```
   *
   * @category Transaction Signing
   */
  async signAndSend(request: SignAndSendRequest): Promise<TransactionSubmissionResponse> {
    try {
      const signRequest: SignRequest = {
        sessionSecrets: request.sessionSecrets,
        transactionPayload: request.transactionPayload,
        session: request.session,
      };
      const transactionResult = await this.signingManager.sign(signRequest);
      const result = await this.send({
        address: request.address,
        signedTransactionPayload: transactionResult,
      });

      return result;
    } catch (error: any) {
      if (error instanceof AuthorizationError) {
        throw error;
      }
      throw new AuthorizationError(
        `Signing failed: ${error.message}`,
        "SIGNING_FAILED"
      );
    }
  }

  /**
   * Submits a previously signed transaction to the Solana blockchain through
   * the Grid API.
   *
   * @param request - Send request containing the signed transaction and account address
   * @returns Promise resolving to transaction submission result with signature
   *
   * @example
   * ```typescript
   * try {
   *   const signedTx = await gridClient.sign({ ... });
   *   const result = await gridClient.send({
   *     signedTransactionPayload: signedTx,
   *     address: 'account-address'
   *   });
   *   console.log('Blockchain signature:', result.transactionSignature);
   * } catch (error) {
   *   if (error instanceof ApiError) {
   *     console.error('Submission failed:', error.message);
   *   }
   * }
   * ```
   *
   * @category Transaction Signing
   */
  async send(request: SendTransactionRequest): Promise<TransactionSubmissionResponse> {
    const { signedTransactionPayload, address } = request;
    const { transaction, kmsPayloads } = signedTransactionPayload;

    // Create KMS payloads with provider information from SignResult
    const submitPayload = {
      transaction,
      kms_payloads: kmsPayloads,
    };

    const response = await this.apiClient.submitTransaction(
      address,
      submitPayload
    );

    return response;
  }

  /**
   * Prepares any Solana transaction for signing by adding necessary account
   * metadata, fee calculations, and smart account integration.
   *
   * @param accountAddress - The account address that will sign the transaction
   * @param request - Transaction preparation request with the raw transaction
   * @param queryParams - Optional query parameters for transaction preparation
   * @returns Promise resolving to prepared transaction payload ready for signing
   *
   * @example
   * ```typescript
   * try {
   *   const preparedTx = await gridClient.prepareArbitraryTransaction(accountAddress, {
   *     transaction: 'base64-encoded-solana-transaction'
   *   });
   *
   *   const signedTx = await gridClient.sign({
   *     sessionSecrets: sessionSecrets,
   *     transactionPayload: preparedTx
   *   });
   * } catch (error) {
   *   if (error instanceof ApiError) {
   *     console.error('Preparation failed:', error.message);
   *   }
   * }
   * ```
   *
   * @category Transaction Processing
   */
  async prepareArbitraryTransaction(
    accountAddress: string,
    request: PrepareArbitraryTransactionRequest,
    queryParams?: PrepareArbitraryTransactionQueryParams
  ): Promise<TransactionResponse> {
    const response = await this.apiClient.prepareArbitraryTransaction(
      accountAddress,
      request,
      queryParams
    );

    const transactionData = response;

    if (!transactionData.data.kmsPayloads) {
      transactionData.data.kmsPayloads = [];
    }

    return transactionData;
  }

  /**
   * Create a proposal
   *
   * @param accountAddress - account address
   * @param request: CreateProposalRequest
   *
   * @returns Promise resolving to proposal response
   *
   * @example
   * ```typescript
   * try {
   *   const proposal = await gridClient.createProposal(transaction, signer);
   * } catch (error) {
   *   console.error('Proposal creation failed:', error);
   * }
   * ```
   */
  async createProposal(accountAddress: string, request: CreateProposalRequest): Promise<CreateProposalResponse> {
    const response = await this.apiClient.createProposal(accountAddress, request);
    return response;
  }

  /**
   * Vote on a proposal
   *
   * @param accountAddress - Smart account address
   * @param proposalAddress - Proposal address
   * @param request - Vote proposal request
   *
   * @returns Promise resolving to vote proposal response
   *
   * @example
   * ```typescript
   * try {
   *   const voteResult = await gridClient.voteProposal(
   *     accountAddress,
   *     proposalAddress,
   *     {
   *       signers: ['signer-pubkey-1', 'signer-pubkey-2'],
   *       action: 'approve'
   *     }
   *   );
   * } catch (error) {
   *   console.error('Vote failed:', error);
   * }
   * ```
   */
  async voteProposal(
    accountAddress: string,
    proposalAddress: string,
    request: VoteProposalRequest
  ): Promise<VoteProposalResponse> {
    const response = await this.apiClient.voteProposal(accountAddress, proposalAddress, request);
    return response;
  }

  /**
   * Execute a proposal
   *
   * @param accountAddress - Smart account address
   * @param proposalAddress - Proposal address
   * @param request - Execute proposal request
   *
   * @returns Promise resolving to execute proposal response
   *
   * @example
   * ```typescript
   * try {
   *   const executeResult = await gridClient.executeProposal(
   *     accountAddress,
   *     proposalAddress,
   *     { signer: 'signer-pubkey' }
   *   );
   * } catch (error) {
   *   console.error('Execute failed:', error);
   * }
   * ```
   */
  async executeProposal(
    accountAddress: string,
    proposalAddress: string,
    request: ExecuteProposalRequest
  ): Promise<ExecuteProposalResponse> {
    const response = await this.apiClient.executeProposal(accountAddress, proposalAddress, request);
    return response;
  }
}
